// AGC: AuthContext — Compatibility wrapper around Zustand auth store
// AGC: Preserves the AuthProvider + useAuthContext() API so all consumers work unchanged
// AGC: Internal state management delegates to services/authStore.ts (Zustand)
// AGC: In a future cleanup, consumers can import useAuthStore directly

import React, { createContext, useContext, useCallback, ReactNode } from 'react'
import { useAuthStore } from '../services/authStore'
import type { AuthUser, LoginCredentials, RegisterData, UserRole } from '../types/user'

// AGC: Auth context type (same as before — backward compatible)
interface AuthContextType {
  user: AuthUser | null
  isLoading: boolean
  isAuthenticated: boolean
  error: string | null
  isFirstLogin: boolean
  loginWithGoogle: () => Promise<void>
  loginWithApple: () => Promise<void>
  loginWithEmail: (credentials: LoginCredentials) => Promise<void>
  loginWithPhone: (phoneNumber: string, containerId: string) => Promise<void>
  verifyOTP: (otp: string) => Promise<void>
  register: (data: RegisterData) => Promise<void>
  logout: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  clearError: () => void
  setFirstLoginComplete: () => void
  getAuthToken: () => Promise<string | null>
  refreshProfile: () => Promise<void>
  hasRole: (role: UserRole | UserRole[]) => boolean
  canAccessAdmin: () => boolean
  canManageUsers: () => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

/**
 * AGC: AuthProvider — thin wrapper that delegates to Zustand auth store
 * AGC: Kept for backward compatibility; Zustand store runs independently of this provider
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // AGC: Read all state from the Zustand store
  const {
    user,
    isLoading,
    error,
    isFirstLogin,
    loginWithGoogle,
    loginWithApple,
    loginWithEmail,
    loginWithPhone,
    verifyOTP,
    register,
    logout,
    resetPassword,
    clearError,
    setFirstLoginComplete,
    getAuthToken,
    refreshProfile,
  } = useAuthStore()

  // AGC: Role helpers (derived from user state)
  const hasRole = useCallback((role: UserRole | UserRole[]): boolean => {
    if (!user?.profile) return false
    if (Array.isArray(role)) return role.includes(user.profile.role)
    return user.profile.role === role
  }, [user])

  const canAccessAdmin = useCallback((): boolean => {
    return user?.profile?.role === 'admin'
  }, [user])

  const canManageUsers = useCallback((): boolean => {
    if (!user?.profile) return false
    return ['admin', 'manager', 'submanager'].includes(user.profile.role)
  }, [user])

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    error,
    isFirstLogin,
    loginWithGoogle,
    loginWithApple,
    loginWithEmail,
    loginWithPhone,
    verifyOTP,
    register,
    logout,
    resetPassword,
    clearError,
    setFirstLoginComplete,
    getAuthToken,
    refreshProfile,
    hasRole,
    canAccessAdmin,
    canManageUsers,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * AGC: Hook to use Auth context (same API as before)
 */
export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('AGC: useAuthContext must be used within AuthProvider')
  }
  return context
}

export default AuthProvider
